import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { history } from '@umijs/max';

/** 分享页等场景：不携带 Authorization */
const PUBLIC_API_PATHS = [
  '/api/login',
  '/api/login/captcha',
  '/api/doc',
  '/api/sign',
  '/api/site/getSetting',
  '/api/external/getInclusionShare',
  '/api/external/scheduleArticleKeywords',
  '/api/mediaRelease/preview',
];

const isPublicApiRequest = (url?: string, publicApi?: boolean): boolean => {
  if (publicApi) {
    return true;
  }
  if (!url) {
    return false;
  }
  const path = url.split('?')[0];
  return PUBLIC_API_PATHS.some((p) => path === p || path.endsWith(p));
};

const isSharePage = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/share');

/**
 * @name 错误处理
 * GEO系统的错误处理方案
 * code: 1=成功, 0=失败, 500=系统错误
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { code, msg, data } = res as any;
      
      // code不为1时，认为是错误
      if (code !== 1) {
        const error: any = new Error(msg || '请求失败');
        error.name = 'BizError';
        error.info = { code, msg, data };
        throw error;
      }
    },
    
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      
      // 我们的 errorThrower 抛出的错误
      if (error.name === 'BizError') {
        const errorInfo = error.info;
        if (errorInfo) {
          const { msg, code } = errorInfo;
          
          // 根据code进行不同的错误处理
          if (code === 500) {
            // 系统错误
            notification.error({
              message: '系统错误',
              description: msg || '系统发生错误，请稍后重试',
            });
          } else if (code === -1) {
            // 需要重新登录（分享页为公开访问，不跳转登录）
            const currentPath = window.location.pathname;
            if (currentPath !== '/user/login' && !isSharePage()) {
              message.error('登录已过期，请重新登录');
              // 清除token
              localStorage.removeItem('token');
              history.push('/user/login');
            }
          } else if (code === 0) {
            // 业务错误
            message.error(msg || '操作失败');
          } else if (code === 401) {
            // 未授权，跳转到登录页（但不要在登录页本身跳转）
            const currentPath = window.location.pathname;
            if (currentPath !== '/user/login') {
              message.error('登录已过期，请重新登录');
              localStorage.removeItem('token');
              history.push('/user/login');
            }
          } else if (code === 403) {
            // 无权限
            message.error('您没有权限执行此操作');
          } else {
            // 其他错误
            message.error(msg || '请求失败');
          }
        }
      } else if (error.response) {
        // Axios 错误 - HTTP状态码错误
        const status = error.response.status;
        const currentPath = window.location.pathname;
        
        switch (status) {
          case 401:
            if (currentPath !== '/user/login' && !isSharePage()) {
              message.error('登录已过期，请重新登录');
              localStorage.removeItem('token');
              history.push('/user/login');
            }
            break;
          case 403:
            message.error('您没有权限访问');
            break;
          case 404:
            message.error('请求的资源不存在');
            break;
          case 500:
            message.error('服务器错误');
            break;
          default:
            message.error(`请求错误: ${status}`);
        }
      } else if (error.request) {
        // 请求已发出但没有收到响应
        message.error('网络错误，请检查网络连接');
      } else {
        // 请求配置出错
        message.error('请求失败，请重试');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const publicApi = (config as RequestOptions & { publicApi?: boolean }).publicApi;
      if (isPublicApiRequest(config.url, publicApi)) {
        return config;
      }
      // 新窗口代登录使用 sessionStorage，避免覆盖当前标签页 localStorage
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，统一处理
      const { data } = response as any;
      
      // 如果data中有code字段，按照GEO系统规范处理
      if (data && typeof data.code !== 'undefined') {
        if (data.code === 1) {
          // 成功，直接返回
          return response;
        } else {
          // 失败，抛出错误
          const error: any = new Error(data.msg || '请求失败');
          error.name = 'BizError';
          error.info = { 
            code: data.code, 
            msg: data.msg, 
            data: data.data 
          };
          throw error;
        }
      }
      
      return response;
    },
  ],
  
  // 跨域请求携带凭证
  withCredentials: true,
};
